import type { View, ViewContext } from '../router';
import { el, ensureStyle } from '../dom';
import { icon } from '../components/icons';
import {
  backBtn,
  emptyFocus,
  exitButton,
  ensureQuizStyle,
  fillProgress,
  focusSetup,
  focusTag,
  hearOthers,
  keyLegend,
  lengthField,
  markChoices,
  newBirdsField,
  missedList,
  quizHeader,
  quizKeys,
  retryTag,
  segmented,
  tagSlot,
  afterBar,
  compareButton,
  dontKnowButton,
  undoButton,
  type HearHandle,
  type KeyActions,
  type Miss,
} from '../components/quiz-chrome';
import { waveformPlayer } from '../components/waveform-player';
import {
  buildSoundSession,
  soundPool,
  WITHOUT_CALLS,
  type SoundQuestion,
} from '../../core/quiz/sound';
import { focusCodes, narrow, parseFocus } from '../../core/quiz/focus';
import { buildSoundPairSession } from '../../core/quiz/pairs';
import { reaskSound } from '../../core/quiz/retry';
import { getSpecies } from '../../core/region';
import { studyCodes } from '../../core/ignored';
import { ratingFromCorrect } from '../../core/fsrs';
import { clearUndo, gradeAnswer, undoAnswer } from '../../core/undo';
import { haptic } from '../haptics';
import {
  newBirdShare,
  retryMisses,
  ROUND_LENGTHS,
  roundLength,
  setNewBirdShare,
  setRoundLength,
  setSoundWithCalls,
  soundWithCalls,
} from '../../core/quiz/settings';
import {
  HARD_SOUND_MASTERY,
  masteryBySkill,
  soundHardUnlocked,
  XP_PER_CORRECT,
} from '../../core/progress';
import { t } from '../../strings';

ensureQuizStyle();

ensureStyle(
  'quiz-sound-css',
  `
  /* Hard mode changes what you hear, so it has to change what you see. The tag
     rides the header row beside the counter now (.bar .tags in the shared
     chrome) rather than a centred line of its own — the line was costing the
     answer list about 40px of a phone screen to carry two words. */
  .quiz .hardtag { color:var(--accent); border:1.5px solid currentColor;
    border-radius:999px; padding:3px 8px; font-size:8.4px; letter-spacing:.1em;
    line-height:1; flex:none; }
`,
);

/** What a round is allowed to play. `all` is the hard mode: calls included. */
type Voices = 'songs' | 'all';

const VOICES: { value: Voices; label: string; note: string }[] = [
  { value: 'songs', label: t('quiz.voices.songs'), note: t('quiz.voices.songsNote') },
  { value: 'all', label: t('quiz.voices.all'), note: t('quiz.voices.allNote') },
];

export const quizSoundView: View = async (root: HTMLElement, ctx: ViewContext) => {
  // Calls are far harder to place than songs, so clearing the mastery bar lets
  // them into the mix by default — but only as a default. It used to be the
  // whole story: the unlock forced hard mode from then on, with no way to try it
  // early and no way back to the songs afterwards. Now it decides which option
  // this screen opens on, and the learner decides the rest.
  const unlocked = soundHardUnlocked(await masteryBySkill(studyCodes()));
  const [callsDefault, savedLen, savedShare] = await Promise.all([
    soundWithCalls(unlocked),
    roundLength('sound'),
    newBirdShare('sound'),
  ]);
  let voices: Voices = callsDefault ? 'all' : 'songs';
  // How many birds this round asks about; opens on whatever the last Sound ID
  // round was run with.
  let len = savedLen;
  // How much of the round is held for birds this learner has never met, as a
  // share of its length; see core/quiz/settings.ts.
  let share = savedShare;
  // Whether the round IN PROGRESS has calls in it. Fixed when that round was
  // built, so a question can never disagree with the recording it is playing.
  let hard = voices === 'all';
  // The pair drill: same screen, same grading, but the round is built from the
  // birds this learner has actually been calling each other rather than from
  // the schedule. See core/quiz/pairs.ts.
  const drill = ctx.params.drill === 'pairs';
  // A round started from the Library is confined to the birds that were on
  // screen when its button was pressed — see core/quiz/focus.ts. The drill has
  // a pool of its own and the two never combine. The focus lives in the hash
  // and nowhere else, so a reload keeps it and leaving the round drops it.
  const focus = drill ? null : parseFocus(ctx.params.focus);
  const pool = focus ? focusCodes(focus) : null;
  const wrap = el('div.quiz.sound', {});
  root.append(wrap);

  let session: SoundQuestion[] = [];
  // How many questions the round was dealt. Anything past it is a miss come
  // back for another go — see `retryMisses` — and is practice, not scored.
  let size = 0;
  let index = 0;
  let retryOn = false;
  let correctCount = 0;
  // The species this round got wrong, in the order they came up — the list the
  // end of a round is actually about, each one carrying the voice it was asked
  // in so the scorecard can play it back.
  let missed: Miss[] = [];
  let disposed = false;
  let currentPlayer: { stop: () => void } | null = null;
  // Every speaker on screen — one per bird that isn't the answer. Held as a
  // list rather than a single handle because a question now offers three.
  let currentSpeakers: HearHandle[] = [];

  // One key listener for the whole visit, reading whatever the screen currently
  // offers. A listener added per render is a listener leaked per render.
  let keys: KeyActions | null = null;
  const releaseKeys = quizKeys(wrap, () => keys);

  // Only the first round of a visit inherits the daily card's hand-off.
  let firstRound = true;

  /** What the round about to start is, in the length currently chosen. */
  function blurbText(): string {
    if (drill) {
      return t('quiz.sound.setup.drillBlurb', { n: len });
    }
    // An ordinary round gets no line at all. It used to promise "N species from
    // your review queue, drawn by the same schedule as <other mode>", which read
    // as a technical note about the scheduler on the one screen whose job is to
    // start a round — the title, the length and the options say everything a
    // learner needs here. The drill above keeps its line because a drill is NOT
    // an ordinary round, and nothing else on the screen says so.
    return '';
  }

  /**
   * The screen every visit opens on: how long the round is, and which voices it
   * is allowed to use.
   */
  function renderSetup(): void {
    keys = null;
    currentPlayer?.stop();
    for (const speaker of currentSpeakers) speaker.stop();
    currentSpeakers = [];
    // Held onto so picking a length can rewrite the promise in place. Repainting
    // the whole screen would drop focus off the button just pressed.
    const text = blurbText();
    const blurb = text ? el('p.blurb.meta', {}, text) : null;
    // Which voices are allowed changes how many of the focused birds this mode
    // can ask about at all, so the note and the ladder are rebuilt when it does
    // — the same swap-in-place the length button does to the blurb, for the
    // same reason.
    const askable = () =>
      pool ? narrow(soundPool(voices === 'all' ? undefined : WITHOUT_CALLS), pool) : null;
    let setup = focusSetup(focus, askable(), len, t('quiz.sound.setup.lacking'));
    len = setup.len;
    let lenRow = setup.empty ? el('span.focusslot', {}) : lengthField(len, (n) => (len = n), setup.lengths);
    const note = el('div.focusslot', {}, setup.note);
    wrap.replaceChildren(
      el('div.bar', {}, backBtn(ctx)),
      el(
        'div.setup',
        {},
        el('h1', {}, t('common.mode.sound')),
        blurb,
        note,
        lenRow,
        // Not in a drill: that round is built from the birds this learner keeps
        // confusing, which are by definition birds they have already met.
        drill || setup.empty ? null : newBirdsField(share, (v) => (share = v)),
        el(
          'div.field',
          {},
          el('div.prompt.label', {}, t('quiz.voices.label')),
          segmented(VOICES, voices, (v) => {
            voices = v;
            if (!focus) return;
            setup = focusSetup(focus, askable(), len, t('quiz.sound.setup.lacking'));
            len = setup.len;
            note.replaceChildren(...(setup.note ? [setup.note] : []));
            const next = setup.empty
              ? el('span.focusslot', {})
              : lengthField(len, (n) => (len = n), setup.lengths);
            lenRow.replaceWith(next);
            lenRow = next;
          }),
          // Said here rather than on the option itself, because it explains why
          // the harder of the two is already chosen.
          unlocked
            ? el('div.hint', {}, t('quiz.voices.unlocked', { pct: HARD_SOUND_MASTERY }))
            : null,
        ),
        setup.empty
          ? emptyFocus(ctx)
          : el(
              'button.next.btn.btn-primary',
              { onclick: () => void start() },
              t('quiz.round.start'),
              icon('arrow-right', { size: 18 }),
            ),
      ),
    );
  }

  async function start(): Promise<void> {
    // A length that came off a focus is a fact about how many sparrows there
    // are, not a preference — remembering it would hand the next ordinary round
    // a number the learner never chose.
    if (ROUND_LENGTHS.includes(len)) await setRoundLength('sound', len);
    await setNewBirdShare('sound', share);
    await setSoundWithCalls(voices === 'all');
    hard = voices === 'all';
    // Two ways a round gets built. The drill takes its species from the mix-up
    // record and ignores the schedule (and the daily hand-off with it — nothing
    // sends a learner into a drill mid-round).
    //
    // Otherwise: the daily card grades its own answer and then sends the learner
    // here, naming the bird it asked as `?done=`. That species sits out this
    // round and the round is one question shorter, so the hand-off reads as one
    // round rather than the same bird twice and one question over. A second
    // round started from the done screen is the full length again.
    const allowed = hard ? undefined : WITHOUT_CALLS;
    session = drill
      ? await buildSoundPairSession(new Date(), Math.random, allowed, len)
      : await buildSoundSession(
          new Date(),
          Math.random,
          allowed,
          firstRound ? ctx.params.done : undefined,
          len,
          pool ?? undefined,
        );
    firstRound = false;
    retryOn = await retryMisses();
    size = session.length;
    index = 0;
    correctCount = 0;
    missed = [];
    render();
  }

  function render(): void {
    currentPlayer?.stop();
    for (const speaker of currentSpeakers) speaker.stop();
    currentSpeakers = [];
    keys = null;
    if (disposed) return;
    wrap.replaceChildren();
    if (!session.length) return renderEmpty();
    if (index >= session.length) return renderDone();
    renderQuestion(session[index]);
  }

  function renderQuestion(q: SoundQuestion): void {
    const header = quizHeader(ctx, index, session.length, t('quiz.round.nameTheBird'));

    const player = waveformPlayer(q.audio);
    currentPlayer = player;
    void player.toggle(); // autoplay

    // Which of the bird's three voices this clip is. Hidden until the answer is
    // in — before it, naming the sound type narrows the field for free — but
    // shown in both modes, since neither one deals a single voice any more.
    const typeTag = el(
      'span.typetag.label',
      { title: t('quiz.round.recordingType', { type: q.audio.type }) },
      t('quiz.round.soundType', { type: q.audio.type }),
    );
    typeTag.hidden = true;

    const choices = el('div.choices', {});
    let answered = false;
    // What they named, so undo knows which way the tally moved.
    let picked = '';
    // A miss come back: answered for practice, never scored or undone.
    const comeback = index >= size;
    // Whether answering sent this question to the back of the round.
    let requeued = false;
    const buttons: HTMLButtonElement[] = [];
    const rows: HTMLElement[] = [];

    for (const code of q.choices) {
      const sp = getSpecies(code);
      const btn = el(
        'button.choice.btn.btn-quiet',
        {},
        el('div', {}, sp?.common ?? code),
        el('div.sci', {}, sp?.sci ?? ''),
      ) as HTMLButtonElement;
      buttons.push(btn);
      btn.addEventListener('click', () => void settle(code, buttons.indexOf(btn)));
      // Wrapped, so a wrong pick can grow a speaker beside it (see hearIt): the
      // choice itself is disabled the moment it is answered, and a disabled
      // button swallows every click inside it.
      const row = el('div.choicerow', {}, btn);
      rows.push(row);
      choices.append(row);
    }

    const nextBtn = el(
      'button.next.btn.btn-primary',
      {},
      t('quiz.round.next'),
      icon('arrow-right', { size: 18 }),
    ) as HTMLButtonElement;
    nextBtn.hidden = true;
    nextBtn.addEventListener('click', () => {
      // The question just answered is done being undoable the moment the round
      // moves past it.
      clearUndo();
      index += 1;
      render();
    });

    const undoBtn = undoButton(async () => {
      if (!answered || !(await undoAnswer())) return;
      haptic('undo');
      // The round's own tally has to come back with the card: it is what the
      // scorecard at the end reports, and it is kept here rather than counted
      // from the log.
      if (picked === q.target.code) correctCount -= 1;
      else missed.pop();
      if (requeued) session.pop();
      // Re-rendering the same index rebuilds this question untouched — same
      // recording, same choices in the same order — because a question is data
      // in `session` and the screen is only ever a drawing of it.
      render();
    });

    // The bird asked about against the bird named instead, opened on the voice
    // the question played — the pair is the whole content of a wrong answer.
    const compare = compareButton();

    const dontKnow = dontKnowButton(() => settle(null, -1));

    const actions: KeyActions = {
      choices: buttons,
      replay: () => void player.toggle(),
      next: nextBtn,
      undo: undoBtn,
      skip: dontKnow,
    };
    keys = actions;

    /**
     * Everything answering does, whether a bird was named or not.
     *
     * `code` is null for "I don't know", and `at` is -1 with it — which
     * `markChoices` already paints correctly: the answer fills in, the rest dim,
     * and nothing is marked as the wrong pick, because nothing was picked.
     */
    async function settle(code: string | null, at: number): Promise<void> {
      if (answered) return;
      answered = true;
      picked = code ?? '';
      player.stop();
      const correct = code === q.target.code;
      // Before anything is awaited: the buzz is meant to land with the tap.
      haptic(correct ? 'right' : 'wrong');
      requeued = !correct && retryOn;
      if (requeued) session.push(reaskSound(q));
      if (comeback) {
        // Nothing to tally.
      } else if (correct) correctCount += 1;
      else missed.push({ code: q.target.code, type: q.audio.type });
      markChoices(buttons, q.choices, at, q.target.code);
      // The bar has to count the question just answered, or the last question of
      // a round sits at 90% while its result is on screen.
      fillProgress(header, index, session.length);
      typeTag.hidden = false;
      dontKnow.hidden = true;
      // Every bird that wasn't the answer, in the voice they were just played —
      // silent until one is asked for. The one they named is among them, and on
      // a right answer so are all three of the ones they rejected.
      currentSpeakers = hearOthers(
        rows,
        q.choices,
        q.target.code,
        q.audio.type,
        code ?? undefined,
      );
      // Nothing to hold this bird against when no bird was named.
      if (code) compare.offer(q.target.code, code, q.audio.type);
      // `code` — the bird they named, right or wrong — is what makes the
      // mix-up list and this very drill possible. A skip names none, and is
      // deliberately absent from that record: not knowing is not confusing.
      if (!comeback) {
        await gradeAnswer({
          code: q.target.code,
          skill: 'sound',
          rating: ratingFromCorrect(correct),
          picked: code ?? undefined,
          xp: correct ? XP_PER_CORRECT : 0,
        });
      }
      if (!disposed) {
        nextBtn.hidden = false;
        undoBtn.hidden = comeback;
      }
      nextBtn.replaceChildren(
        index + 1 >= session.length
          ? t('quiz.round.finish')
          : t('quiz.round.next'),
        icon('arrow-right', { size: 18 }),
      );
    }

    tagSlot(header).append(
      ...(focus ? [focusTag(focus, size)] : []),
      ...(comeback ? [retryTag()] : []),
      ...(drill
        ? [
            el(
              'span.hardtag.label',
              { title: t('quiz.sound.question.mixUpsTitle') },
              t('quiz.sound.question.mixUps'),
            ),
          ]
        : []),
      ...(hard
        ? [
            el(
              'span.hardtag.label',
              { title: t('quiz.round.withCallsTitle') },
              t('quiz.round.withCalls'),
            ),
          ]
        : []),
      typeTag,
    );

    wrap.append(
      header,
      player.element,
      el('div.hint', {}, t('quiz.round.replayHint')),
      choices,
      dontKnow,
      keyLegend(actions),
      afterBar(undoBtn, nextBtn, compare.element),
    );
  }

  function renderEmpty(): void {
    wrap.append(
      el('div.bar', {}, backBtn(ctx)),
      el(
        'div.done',
        {},
        el('div.big', {}, t('quiz.round.nothingToStudy')),
        el(
          'div.stat',
          {},
          drill
            ? t('quiz.sound.empty.drill')
            : hard
              ? t('quiz.sound.empty.noAudio')
              : t('quiz.round.noSongs'),
        ),
        // Leaving calls out can be chosen at any point now, and in a thin pack
        // it is the likeliest reason a round came back empty — so the way out of
        // this screen is the screen that set it.
        drill
          ? null
          : el(
              'button.next.btn.btn-quiet',
              { onclick: () => renderSetup() },
              t('quiz.round.changeOptions'),
            ),
      ),
    );
  }

  function renderDone(): void {
    const pct = Math.round((correctCount / size) * 100);
    const again = el(
      'button.next.btn.btn-primary',
      { onclick: () => void start() },
      t('quiz.round.again'),
      icon('arrow-right', { size: 18 }),
    ) as HTMLButtonElement;
    keys = { next: again };
    // The misses play where they stand, so their speakers join the round's own
    // and are silenced by the same teardown.
    const panel = missedList(missed, ctx);
    if (panel) currentSpeakers = panel.speakers;
    wrap.append(
      el('div.bar', {}, backBtn(ctx)),
      el(
        'div.done',
        {},
        el(
          'div.big',
          {},
          t('quiz.round.correct', { scored: correctCount, total: size }),
        ),
        el(
          'div.stat',
          {},
          t('quiz.round.stat', { pct, xp: correctCount * XP_PER_CORRECT }),
        ),
        panel?.element ?? null,
        again,
        el(
          'button.next.btn.btn-quiet',
          { onclick: () => renderSetup() },
          t('quiz.round.changeOptions'),
        ),
        exitButton(ctx, Boolean(focus)),
      ),
    );
  }

  // `?go=1` — a round started from the Home card, which already knows what the
  // last one was set to. The setup screen is three taps between a learner and
  // the round they have already asked for; "Change options" at the end of it is
  // the way back to one.
  if (ctx.params.go) void start();
  else renderSetup();
  return () => {
    disposed = true;
    releaseKeys();
    // Leaving a round mid-answer takes the undo with it: the question it
    // belonged to is off the screen, so nothing may reach back to it.
    clearUndo();
    currentPlayer?.stop();
    for (const speaker of currentSpeakers) speaker.stop();
  };
};
